import {
  //badgeRecipe,
  buttonRecipe as baseButtonRecipe,
  skeletonRecipe as baseSkeletonRecipe,
  //skipNavLinkRecipe,
  spinnerRecipe as baseSpinnerRecipe,
  checkmarkRecipe,
  //codeRecipe,
  //colorSwatchRecipe,
  containerRecipe,
  headingRecipe,
  iconRecipe,
  //inputAddonRecipe,
  // inputRecipe,
  //kbdRecipe,
  linkRecipe,
  markRecipe,
  radiomarkRecipe,
  separatorRecipe,
  //textareaRecipe,
} from '@chakra-ui/react/theme';

import { pressFeedback } from '../pressFeedback';
import { chipRecipe } from './chip';
import { inputRecipe } from './input';
import { inputAddonRecipe } from './input-addon';
import { navigationItemRecipe } from './navigation-item';
import { panelRecipe } from './panel';
import { textareaRecipe } from './textarea';

// `md` is the default size, so this is what you get without asking — and at 40px it
// lands 4px under the 44px tap minimum on touch. `lg` is already 44px; the denser
// sizes (2xs/xs/sm) are left alone on purpose: growing them would wreck comment
// density, so they need an expanded hit area instead, which has to be done per-case
// where we can see what a widened target would overlap.
//
// `any-pointer`, not `pointer`: an iPad on a Magic Keyboard reports the trackpad as
// its primary pointer while the touchscreen is still there. Desktop mice never match,
// so this is inert on desktop.
//
// Both min-* are required. IconButton has no recipe of its own in Chakra v3 — it is
// literally <Button px="0" py="0"> — so min-height alone would stretch every square
// icon button into an ellipse.
const coarseTapTarget = {
  '@media (any-pointer: coarse)': { minHeight: '44px', minWidth: '44px' },
};

// Подсветка под курсором у тихих кнопок — полупрозрачная ступень, а не сплошной цвет палитры.
//
// Стоковый рецепт красит ghost и outline под курсором в `colorPalette.subtle`. Для фиолетовой
// палитры (а она у кнопки по умолчанию) это `bg.card` — сплошной цвет карточки в покое. Когда
// такая кнопка лежит на карточке, которая сама поднимается под курсором, кнопка остаётся на
// старом цвете и читается как дырка: карточка светлеет, кнопка темнеет. Для серой палитры это
// стоковый `#27272a` — нейтральный серый, которого в нашей теме нет вовсе.
//
// `bg.muted` — белый на 6% — поднимает кнопку на одну ступень над ЛЮБОЙ подложкой, в ту же
// сторону и тем же цветом, что строку списка и иконочную кнопку. Одно имя на всё, что светлеет.
const liftOnHover = { _hover: { bg: 'bg.muted' }, _expanded: { bg: 'bg.muted' } };

/*
  Отклик на нажатие: кнопка проседает на 3% и возвращается.

  До этого в продукте было 93 файла с подсветкой под курсором и 4 с реакцией на нажатие. На
  десктопе это полбеды, а на телефоне наведения не существует вовсе: палец давит на кнопку,
  и до ответа сервера не происходит ничего. Android-сборка уже в сторе, так что это не
  украшение, а единственное подтверждение, что нажатие вообще услышали.

  Здесь, в `base`, а не в вариантах: проседает и solid, и outline, и ghost, и subtle, и
  IconButton заодно - в Chakra v3 у него нет своего рецепта, он буквально `<Button px="0">`.

  0.97, а не 0.95: scale тянет за собой и содержимое, а на кнопке с иконкой и подписью
  заметная просадка читается как подёргивание.

  Свойства перечислены поимённо, и это не вкусовщина. Стоковая ступень `common` заявляет
  `transform` в своём списке, но в браузере на кнопке доезжают только первые шесть свойств
  (`background-color, border-color, color, fill, stroke, opacity`), а кривая остаётся стоковым
  `ease` мимо нашего токена. Замерено на живой странице: просадка срабатывала мгновенно, без
  перехода. Пока полагались на `common`, отклика на нажатие фактически не было - была только
  запись о нём в теме.

  Цвет держим в том же списке: своя строка `transition` заменяет стоковую целиком, и без него
  подсветка под курсором перестала бы ехать.

  Выключенная кнопка не проседает: она ничего не услышала.
*/
const buttonRecipe = {
  ...baseButtonRecipe,
  base: {
    ...baseButtonRecipe.base,
    fontWeight: '600',
    colorPalette: 'purple',
    ...pressFeedback,
    _disabled: { ...baseButtonRecipe.base?._disabled, ...pressFeedback._disabled },
  },
  variants: {
    ...baseButtonRecipe.variants,
    variant: {
      ...baseButtonRecipe.variants?.variant,
      ghost: { ...baseButtonRecipe.variants?.variant?.ghost, ...liftOnHover },
      /*
        Рамка от нашей темы, а не из палитры.

        Сток берёт `colorPalette.muted`, и на фиолетовой палитре это сплошной `rgb(37,39,47)` -
        цвет, который про наши поверхности ничего не знает. Замерено: на фоне страницы рамка
        отличалась от подложки в 1.25 раза, на карточке в 1.12, а на `bg.raised` ровно в 1.00,
        то есть пропадала. Наш `border` - белый с прозрачностью - держит 1.23-1.28 на всех
        пяти, потому что поднимается относительно того, на чём лежит. То же правило, что у
        кружка подсказки.
      */
      outline: {
        ...baseButtonRecipe.variants?.variant?.outline,
        ...liftOnHover,
        borderColor: 'border',
      },
    },
    size: {
      ...baseButtonRecipe.variants?.size,
      md: { ...baseButtonRecipe.variants?.size?.md, fontSize: 'md', ...coarseTapTarget },
    },
  },
};

const spinnerRecipe = {
  ...baseSpinnerRecipe,
  base: { ...baseSpinnerRecipe.base, color: 'purple.400' },
};

// Скелетон по умолчанию заливается bg.emphasized (brand.600) — это СВЕТЛЕЕ карточки, которую
// он подменяет (bg.card = brand.700). Из-за этого пустая лента выглядела ярче заполненной.
// Приглушаем: своя заливка чуть выше фона страницы и более медленный, менее контрастный пульс.
const skeletonRecipe = {
  ...baseSkeletonRecipe,
  variants: {
    ...baseSkeletonRecipe.variants,
    variant: {
      ...baseSkeletonRecipe.variants?.variant,
      pulse: {
        ...baseSkeletonRecipe.variants?.variant?.pulse,
        background: 'bg.skeleton',
        // 1.2s дёргает глаз на пустом экране; 2s читается как «идёт загрузка», а не как мигание.
        animationDuration: 'var(--duration, 2s)',
      },
      /*
        Бегущий блик - для заглушек крупной формы (карточка заявки), где пульс прозрачности
        по почти прозрачной заливке не виден вовсе. Цвета из темы (`bg.skeleton.shine`), темп
        как у пульса: сток бежит пять секунд, и за время холодного старта блик не успевал
        пройти и раза.
      */
      shine: {
        ...baseSkeletonRecipe.variants?.variant?.shine,
        '--start-color': 'colors.bg.skeleton',
        '--end-color': 'colors.bg.skeleton.shine',
        animation: 'bg-position var(--duration, 2s) ease-in-out infinite',
      },
    },
  },
};

// Линия в один пиксель разъехалась на шесть цветов: whiteAlpha.200, bg.muted, purple.300,
// border.subtle, border и один rgba числом. Разницу между ними не видит никто, но сменить их
// разом было нельзя — цвет проставлял вызывающий. Ставим здесь тот, что и так преобладал
// (border это whiteAlpha.200, 11 мест из 24), и вызывающему больше ничего передавать не нужно.
const separatorRecipeWithBorder = {
  ...separatorRecipe,
  base: { ...separatorRecipe.base, borderColor: 'border' },
};

export const recipes = {
  //badge: badgeRecipe,
  button: buttonRecipe,
  //code: codeRecipe,
  chip: chipRecipe,
  container: containerRecipe,
  heading: headingRecipe,
  input: inputRecipe,
  inputAddon: inputAddonRecipe,
  //kbd: kbdRecipe,
  link: linkRecipe,
  mark: markRecipe,
  navigationItem: navigationItemRecipe,
  panel: panelRecipe,
  separator: separatorRecipeWithBorder,
  skeleton: skeletonRecipe,
  //skipNavLink: skipNavLinkRecipe,
  spinner: spinnerRecipe,
  textarea: textareaRecipe,
  icon: iconRecipe,
  checkmark: checkmarkRecipe,
  radiomark: radiomarkRecipe,
  //colorSwatch: colorSwatchRecipe,
};
